/**
 * The downloadable reply voices, one download per voice: Pocket English
 * everywhere, Pocket German, French, Spanish, Dutch, Czech, Greek or
 * Estonian where the user uses that language, Kokoro v1.1-zh where they use
 * Chinese, and Kokoro v1.0 where they use Italian, Portuguese, Hindi or
 * Japanese. Each download carries every Denker voice, so a voice change
 * never needs one.
 *
 * Rust owns the download (`voice_assets_pocket.rs`) and answers per language;
 * which languages this computer wants is decided here, in
 * `wantedPocketLanguages`, and Rust removes a download no wanted language
 * needs any more. The voice worker picks a language up on its next reply.
 */
import { invoke } from "@tauri-apps/api/core";

import { isTauri } from "@/lib/is-tauri";
import { onAssetProgress, type VoiceAssetProgress } from "./voice-assets";

/** The languages with a voice of their own, each its own download: the
 *  directories of `POCKET_LANGUAGES` in pocket-voices.env other than English
 *  and Kokoro v1.0's. */
export const OWN_VOICE_LANGUAGES = [
  "de",
  "zh",
  "fr",
  "es",
  "nl",
  "cs",
  "el",
  "et",
] as const;

export type PocketLanguage =
  | "en"
  | (typeof OWN_VOICE_LANGUAGES)[number]
  | "kokoro";

/** The languages Kokoro v1.0 speaks: `POCKET_KOKORO_SPEAKS` in
 *  pocket-voices.env, which Rust and the voice worker read. */
export const KOKORO_LANGUAGES = ["it", "pt", "hi", "ja"] as const;

/** Whether Denker can answer aloud in `language`, a primary language tag:
 *  English, one with a voice of its own, or one Kokoro v1.0 speaks. */
export function speaksLanguage(language: string): boolean {
  return ["en", ...OWN_VOICE_LANGUAGES, ...KOKORO_LANGUAGES].some(
    (spoken) => spoken === language,
  );
}

export type PocketVoiceStatus = {
  /** Installed: replies in this language are spoken with it. */
  ready: boolean;
  missing_bytes: number;
};

export type PocketVoiceProgress = VoiceAssetProgress & {
  language: PocketLanguage;
};

/** Where the command does not exist, and in the browser. */
const NOTHING_TO_DO: PocketVoiceStatus = { ready: true, missing_bytes: 0 };

const primaryLanguage = (tag: string | null | undefined) =>
  tag?.trim().split(/[-_]/)[0]?.toLowerCase();

/** The languages the user uses, as tags: what dictation listens for, the
 *  languages they speak (as Settings shows them, the default included), the
 *  app language and their keyboards' languages. */
export type UsedLanguages = {
  dictationLocale: string | null;
  spokenLanguages: readonly string[];
  appLanguage: string;
  keyboardLanguages: readonly string[];
};

/** English always; each language with a voice of its own, then Kokoro v1.0,
 *  when any of the user's languages or the computer's is one they speak. */
export function wantedPocketLanguages(used: UsedLanguages): PocketLanguage[] {
  const computerLanguages =
    typeof navigator === "undefined"
      ? []
      : (navigator.languages ?? [navigator.language]);
  const languages = new Set(
    [
      used.dictationLocale,
      ...used.spokenLanguages,
      used.appLanguage,
      ...used.keyboardLanguages,
      ...computerLanguages,
    ].map(primaryLanguage),
  );
  return [
    "en",
    ...OWN_VOICE_LANGUAGES.filter((lang) => languages.has(lang)),
    ...(KOKORO_LANGUAGES.some((lang) => languages.has(lang))
      ? (["kokoro"] as const)
      : []),
  ];
}

export async function pocketVoiceStatus(
  language: PocketLanguage,
): Promise<PocketVoiceStatus> {
  if (!isTauri()) return NOTHING_TO_DO;
  try {
    return await invoke<PocketVoiceStatus>("denker_pocket_voice_status", {
      language,
    });
  } catch {
    return NOTHING_TO_DO;
  }
}

/** Downloads `language`'s models if they are missing, resolving with its
 *  status after. A second call joins a download already running. */
export function ensurePocketVoice(
  language: PocketLanguage,
): Promise<PocketVoiceStatus> {
  return invoke<PocketVoiceStatus>("denker_pocket_voice_ensure", { language });
}

/** Tells Rust which voices this computer wants, on every change of the
 *  answer: it removes a download nothing here needs any more (Kokoro v1.0,
 *  once none of its languages is used). Resolves once the answer is
 *  recorded, so a download asked for after it is never removed by an older
 *  answer. Best effort: Rust logs a failure. */
export async function setWantedPocketVoices(
  wanted: readonly PocketLanguage[],
): Promise<void> {
  if (!isTauri()) return;
  await invoke("denker_pocket_voices_wanted", { wanted }).catch(() => {});
}

export function onPocketVoiceProgress(
  handler: (progress: PocketVoiceProgress) => void,
): () => void {
  return onAssetProgress<PocketVoiceProgress>("pocket-voice-progress", handler);
}
